import { CalendarPlus, ChevronRight, Loader2, Mail, MessageCircle, Phone } from 'lucide-react';
import { Fragment } from 'react';
import KlarnaBadge from '~/components/new/KlarnaBadge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '~/components/ui/dialog';
import { KLARNA_MIN_CART_TOTAL_EUR } from '~/lib/klarna';
import { cn } from '~/lib/utils';
import { Language, useLanguageStore } from '~/stores/language-store';
import { CUSTOMER_CANCELLATION_REASON_LABELS, CUSTOMER_REASON_ICONS, type CustomerCancellationReason } from '~/types/server-types/AppointmentCancellationReason';

// minmax(0,1fr) keeps wide content (e.g. non-wrapping buttons) from stretching the grid past the dialog on small phones
export const REASON_DIALOG_CLASSES = 'grid-cols-[minmax(0,1fr)] max-sm:p-5 max-sm:top-[25%] max-sm:translate-y-[-25%] max-h-[calc(100dvh-2rem)] overflow-y-auto';
export const DIALOG_CANCEL_BUTTON_CLASSES = 'w-full flex flex-row items-center justify-center px-5 py-3 cursor-pointer text-base text-burgundy font-medium border border-burgundy/60 rounded-full hover:bg-burgundy/5 transition-colors';
export const RESCHEDULE_BUTTON_CLASSES = 'w-full flex flex-row items-center justify-center gap-2 px-5 py-3 text-center text-base text-white font-medium bg-green-600 hover:bg-green-700 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
const CANCEL_BUTTON_CLASSES = 'w-full flex flex-row items-center justify-center gap-2 px-5 py-3 text-center text-base text-white font-medium bg-red-600 hover:bg-red-700 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

type TranslationKey = Parameters<ReturnType<typeof useLanguageStore.getState>['t']>[0];

type RetentionConfig = {
    titleKey: TranslationKey;
    bodyKey: TranslationKey;
    showReschedule: boolean;
    rescheduleLabelKey?: TranslationKey;
    rescheduleStaysOnDetails?: boolean;
    showKlarna?: boolean;
    showBookMamaFacial?: boolean;
    showBookConsultation?: boolean;
    showContactList?: boolean;
};

const WHATSAPP_URL = 'https://wa.me/4915735998959';

const DEFAULT_RETENTION_CONFIG: RetentionConfig = {
    titleKey: 'appointmentRetentionDefaultTitle',
    bodyKey: 'appointmentRetentionDefaultBody',
    showReschedule: false,
};

const RETENTION_CONFIGS: Partial<Record<CustomerCancellationReason, RetentionConfig>> = {
    HEALTH: { titleKey: 'appointmentRetentionHealthTitle', bodyKey: 'appointmentRetentionHealthBody', showReschedule: true },
    SCHEDULING_CONFLICT: { titleKey: 'appointmentRetentionSchedulingConflictTitle', bodyKey: 'appointmentRetentionSchedulingConflictBody', showReschedule: true },
    TREATMENT_ANXIETY: { titleKey: 'appointmentRetentionTreatmentAnxietyTitle', bodyKey: 'appointmentRetentionTreatmentAnxietyBody', showReschedule: false, showContactList: true, showBookConsultation: true },
    FINANCIAL: { titleKey: 'appointmentRetentionFinancialTitle', bodyKey: 'appointmentRetentionFinancialBody', showReschedule: false, showKlarna: true },
    PREGNANCY_OR_BREASTFEEDING: { titleKey: 'appointmentRetentionPregnancyTitle', bodyKey: 'appointmentRetentionPregnancyBody', showReschedule: false, showBookMamaFacial: true },
    BOOKING_ERROR: { titleKey: 'appointmentRetentionBookingErrorTitle', bodyKey: 'appointmentRetentionBookingErrorBody', showReschedule: true, rescheduleLabelKey: 'appointmentAdjustAppointment', rescheduleStaysOnDetails: true, showContactList: true },
    OTHER_CLINIC: { titleKey: 'appointmentRetentionOtherClinicTitle', bodyKey: 'appointmentRetentionOtherClinicBody', showReschedule: false },
    NO_LONGER_NEEDED: { titleKey: 'appointmentRetentionNoLongerNeededTitle', bodyKey: 'appointmentRetentionNoLongerNeededBody', showReschedule: false },
    PRACTITIONER_UNAVAILABLE: { titleKey: 'appointmentRetentionPractitionerUnavailableTitle', bodyKey: 'appointmentRetentionPractitionerUnavailableBody', showReschedule: false, showContactList: true },
};

const LINK_CLASSES = 'font-medium text-burgundy underline underline-offset-2 whitespace-nowrap';

const CancelRetentionDialog = ({
    open,
    onOpenChange,
    reasonCode,
    storePhone,
    storeEmail,
    isCancelling,
    onConfirmCancel,
    onReschedule,
    onBookMamaFacial,
    onBookConsultation,
    onPayWithKlarna,
    klarnaMeetsMinimum = true,
    isStartingKlarna = false,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    reasonCode: CustomerCancellationReason | null;
    storePhone: string;
    storeEmail: string;
    isCancelling: boolean;
    onConfirmCancel: () => void;
    onReschedule: () => void;
    // Only passed when the appointment can still change its services
    onBookMamaFacial?: () => void;
    // Only passed when the appointment can still change its services
    onBookConsultation?: () => void;
    // Passed for the financial popup unless Klarna is already authorized
    onPayWithKlarna?: () => void;
    klarnaMeetsMinimum?: boolean;
    isStartingKlarna?: boolean;
}) => {
    const { language, t } = useLanguageStore();
    const isBusy = isCancelling || isStartingKlarna;

    const config = (reasonCode && RETENTION_CONFIGS[reasonCode]) || DEFAULT_RETENTION_CONFIG;
    const Icon = reasonCode ? CUSTOMER_REASON_ICONS[reasonCode] : null;
    const reasonLabel = reasonCode
        ? (language === Language.GERMAN ? CUSTOMER_CANCELLATION_REASON_LABELS[reasonCode].de : CUSTOMER_CANCELLATION_REASON_LABELS[reasonCode].en)
        : '';

    const body = t(config.bodyKey).replace('{reasonLabel}', reasonLabel);
    const bodyParts = body.split(/(\{phone\}|\{email\})/).map((part, index) => {
        if (part === '{phone}') {
            return <a key={index} href={`tel:${storePhone.replace(/\s/g, '')}`} className={LINK_CLASSES}>{storePhone}</a>;
        }
        if (part === '{email}') {
            return <a key={index} href={`mailto:${storeEmail}`} className={LINK_CLASSES}>{storeEmail}</a>;
        }
        return <Fragment key={index}>{part}</Fragment>;
    });

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className={REASON_DIALOG_CLASSES}>
                <DialogHeader className='items-center text-center sm:text-center'>
                    {Icon && (
                        <div className='flex items-center justify-center w-12 h-12 rounded-full bg-burgundy/10'>
                            <Icon className='w-6 h-6 text-burgundy' />
                        </div>
                    )}
                    <DialogTitle className='text-burgundy'>{t(config.titleKey)}</DialogTitle>
                    <DialogDescription className='text-burgundy/70 text-sm font-light'>
                        {bodyParts}
                    </DialogDescription>
                </DialogHeader>
                {config.showContactList && (
                    <ul className='flex flex-col gap-1.5'>
                        {[
                            { icon: Phone, label: storePhone, href: `tel:${storePhone.replace(/\s/g, '')}` },
                            { icon: Mail, label: storeEmail, href: `mailto:${storeEmail}` },
                            { icon: MessageCircle, label: 'WhatsApp', href: WHATSAPP_URL, external: true },
                        ].map(({ icon: ContactIcon, label, href, external }) => (
                            <li key={href}>
                                <a
                                    href={href}
                                    {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                                    className='group flex flex-row items-center gap-2 px-3 py-1.5 rounded-lg border border-burgundy/50 bg-white text-xs font-medium text-burgundy cursor-pointer hover:bg-burgundy/5 hover:border-burgundy active:bg-burgundy/10 transition-colors'
                                >
                                    <ContactIcon className='w-3.5 h-3.5 shrink-0' />
                                    <span className='flex-1 min-w-0 truncate underline underline-offset-2 decoration-burgundy/40 group-hover:decoration-burgundy'>{label}</span>
                                    <ChevronRight className='w-3.5 h-3.5 shrink-0 text-burgundy/60 group-hover:text-burgundy group-hover:translate-x-0.5 transition-transform' />
                                </a>
                            </li>
                        ))}
                    </ul>
                )}
                <DialogFooter className='flex flex-col gap-2 sm:flex-col'>
                    {config.showReschedule && (
                        <button
                            type='button'
                            onClick={() => config.rescheduleStaysOnDetails ? onOpenChange(false) : onReschedule()}
                            disabled={isBusy}
                            className={RESCHEDULE_BUTTON_CLASSES}
                        >
                            <CalendarPlus className='w-4 h-4' />
                            {t(config.rescheduleLabelKey ?? 'appointmentFindNewTime')}
                        </button>
                    )}
                    {config.showBookMamaFacial && onBookMamaFacial && (
                        <button type='button' onClick={onBookMamaFacial} disabled={isBusy} className={RESCHEDULE_BUTTON_CLASSES}>
                            {t('appointmentBookMamaFacial')}
                        </button>
                    )}
                    {config.showBookConsultation && onBookConsultation && (
                        <button type='button' onClick={onBookConsultation} disabled={isBusy} className={RESCHEDULE_BUTTON_CLASSES}>
                            {t('appointmentBookConsultation')}
                        </button>
                    )}
                    {config.showKlarna && onPayWithKlarna && (
                        <button
                            type='button'
                            onClick={() => { if (klarnaMeetsMinimum) onPayWithKlarna(); }}
                            disabled={isBusy || !klarnaMeetsMinimum}
                            className={RESCHEDULE_BUTTON_CLASSES}
                        >
                            {isStartingKlarna ? <Loader2 className='h-4 w-4 animate-spin shrink-0' /> : <KlarnaBadge />}
                            {klarnaMeetsMinimum
                                ? t('appointmentPayWithKlarna')
                                : t('appointmentKlarnaMinimumNotMet').replace('{amount}', String(KLARNA_MIN_CART_TOTAL_EUR))}
                        </button>
                    )}
                    <button type='button' onClick={onConfirmCancel} disabled={isBusy} className={CANCEL_BUTTON_CLASSES}>
                        {isCancelling && <Loader2 className='h-4 w-4 animate-spin shrink-0' />}
                        {isCancelling ? t('appointmentCancelling') : t('appointmentCancelButton')}
                    </button>
                    <button type='button' onClick={() => onOpenChange(false)} disabled={isBusy} className={cn(DIALOG_CANCEL_BUTTON_CLASSES, 'disabled:opacity-50')}>
                        {t('appointmentDialogCancel')}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default CancelRetentionDialog;
